import { Tooltip } from 'bootstrap';

import Marzipano from 'marzipano';
import Mustache from 'mustache';

const INIT_OPTIONS = {
    initialViewParameters: {
        pitch: 0,
        yaw: 0,
        fov: 1.5707963267948966
    },

    modal: false,

    mouseViewMode: 'drag',

    autorotateEnabled: true,
    fullscreenButton: true,

    viewControlButtons: true,    
    viewControlVelocity: 0.7,
    viewControlFriction: 3,

    tilesPath: null,
    infoHotspots: [],
    hotspotCss: 'pano-tooltip'
}

const CSS_HOTSPOT_ON = 'on';
const CSS_MODAL = 'vt-modal';
const CSS_MODAL_OPEN = 'vt-open';
const CSS_LOADER_SHOW = 'show';

const SELECTOR_MODAL_BTN_CLOSE = '.pano-modal-close';
const SELECTOR_LOADER = '.pano-loader';
const SELECTOR_PANO = '.pano';
const SELECTOR_PANO_NAV = '.pano-nav';
const SELECTOR_BTN_LK_UP = SELECTOR_PANO_NAV + ' .look-up';
const SELECTOR_BTN_LK_DOWN = SELECTOR_PANO_NAV + ' .look-down';
const SELECTOR_BTN_LK_LEFT = SELECTOR_PANO_NAV + ' .look-left';
const SELECTOR_BTN_LK_RIGHT = SELECTOR_PANO_NAV + ' .look-right';
const SELECTOR_BTN_ZOOM_IN = SELECTOR_PANO_NAV + ' .zoom-in';
const SELECTOR_BTN_ZOOM_OUT = SELECTOR_PANO_NAV + ' .zoom-out';

const TEMPLATE_HOTSPOT = `
<button data-toggle="tooltip" data-placement="top" data-html="true" data-original-title="{{content}}"/>
`;
const TEMPLATE_BTN_CLOSE = `
<div class="heading">
    <p class="small-title">Virtual Tour</p>
    <button class="pano-modal-close">
		<i class="mdi mdi-close"></i>
    </button>
</div>
`;

class ABVirtualTourCore {
	target;
	options;
    scene;
    viewer;

    constructor(target, options) {
        this.target = this.getTarget(target);
		this.options = this.getOptions(options);        
    }

	getOptions(options) {
        const newOpt = Object.assign({}, INIT_OPTIONS, options);
        return newOpt;
    }
    getTarget(target) {
        if (typeof target === 'string') {
            return document.querySelector(target);
        }
        return target;
    }	

	init() {
        if (this.options.modal) {
            this.target.classList.add(CSS_MODAL);
            this.createCloseBtn();
        }

		this.bindEvents();
	}

	bindEvents() {
        if (this.options.modal) {
            const buttons = document.querySelectorAll('*[data-virtual-tour-modal=' + this.target.getAttribute('id') + ']');
            if (buttons) {
                buttons.forEach((butt) => {
                    butt.addEventListener('click', this.openModal);
                });
            }
            const closeBtn = this.target.querySelector(SELECTOR_MODAL_BTN_CLOSE);
            closeBtn.addEventListener('click', this.closeModal);
        }
	}
	unbindEvents() {	
        if (this.options.modal) {
            const buttons = document.querySelectorAll('*[data-virtual-tour-modal=' + this.target.getAttribute('id') + ']');
            if (buttons) {
                buttons.forEach((butt) => {
                    butt.removeEventListener('click', this.openModal);
                });
            }
            const closeBtn = this.target.querySelector(SELECTOR_MODAL_BTN_CLOSE);
            closeBtn.removeEventListener('click', this.closeModal);
        }	
	}

    create() {
        //this.loadStart();

        const vOpt = {
            controls: {
                mouseViewMode: this.options.mouseViewMode
            }
        };
        this.viewer = new Marzipano.Viewer(this.target.querySelector(SELECTOR_PANO), vOpt);
        const source = Marzipano.ImageUrlSource.fromString(this.options.image);

        // Create geometry.
        const geometry = new Marzipano.EquirectGeometry([{ width: 4000 }]);

        // Create view.
        const limiter = Marzipano.RectilinearView.limit.traditional(4096, 100*Math.PI/180);
        const view = new Marzipano.RectilinearView(this.options.initialViewParameters, limiter);

        // Create scene.
        this.scene = this.viewer.createScene({
            source: source,
            geometry: geometry,
            view: view,
            pinFirstLevel: true,
        });
        
        this.createHotspots();

        if (this.options.viewControlButtons) {
            this.setControls();
        }

        // Display scene.
        this.scene.switchTo(/*null, () => this.loadEnd()*/);
    }

    destroy() {        
        this.unsetControls();
        this.viewer.destroy();
        this.viewer = null;
        this.scene = null;
    }

    createHotspots() {
        if (this.options.infoHotspots && Array.isArray(this.options.infoHotspots)) {
            this.options.infoHotspots.forEach((hotspot) => {
                var element = this.createHotspot(hotspot);
                this.scene.hotspotContainer().createHotspot(element, { yaw: hotspot.yaw, pitch: hotspot.pitch });                
            });
        }
    }

    createCloseBtn() {
        const rendered = Mustache.render(TEMPLATE_BTN_CLOSE);
        this.target.insertAdjacentHTML('beforeend',rendered);
    }

    setControls() {
        const viewUpElement = this.target.querySelector(SELECTOR_BTN_LK_UP);
        const viewDownElement = this.target.querySelector(SELECTOR_BTN_LK_DOWN);
        const viewLeftElement = this.target.querySelector(SELECTOR_BTN_LK_LEFT);
        const viewRightElement = this.target.querySelector(SELECTOR_BTN_LK_RIGHT);
        const viewInElement = this.target.querySelector(SELECTOR_BTN_ZOOM_IN);
        const viewOutElement = this.target.querySelector(SELECTOR_BTN_ZOOM_OUT);

        const controls = this.viewer.controls();
        controls.registerMethod('upElement',    new Marzipano.ElementPressControlMethod(viewUpElement,     'y', -this.options.viewControlVelocity, this.options.viewControlFriction), true);
        controls.registerMethod('downElement',  new Marzipano.ElementPressControlMethod(viewDownElement,   'y',  this.options.viewControlVelocity, this.options.viewControlFriction), true);
        controls.registerMethod('leftElement',  new Marzipano.ElementPressControlMethod(viewLeftElement,   'x', -this.options.viewControlVelocity, this.options.viewControlFriction), true);
        controls.registerMethod('rightElement', new Marzipano.ElementPressControlMethod(viewRightElement,  'x',  this.options.viewControlVelocity, this.options.viewControlFriction), true);
        controls.registerMethod('inElement',    new Marzipano.ElementPressControlMethod(viewInElement,  'zoom', -this.options.viewControlVelocity, this.options.viewControlFriction), true);
        controls.registerMethod('outElement',   new Marzipano.ElementPressControlMethod(viewOutElement, 'zoom',  this.options.viewControlVelocity, this.options.viewControlFriction), true);        
    }
    unsetControls() {
        const controls = this.viewer.controls();
        controls.unregisterMethod('upElement');
        controls.unregisterMethod('downElement');
        controls.unregisterMethod('leftElement');
        controls.unregisterMethod('rightElement');
        controls.unregisterMethod('inElement');
        controls.unregisterMethod('outElement');  
    }

    createHotspot(hotspot) {
        const element = document.createElement('div');
        element.classList.add('pano-hotspot');
        element.innerHTML = Mustache.render(TEMPLATE_HOTSPOT, { content: hotspot.content });
        //element.addEventListener('click', this.onHotspotClick);
        new Tooltip(element.querySelector('button'), { customClass: this.options.hotspotCss, container: element });
        return element;
    }

    onHotspotClick = (evt) => {
        evt.preventDefault();
        const hotspot = evt.currentTarget;
        if (hotspot.classList.contains(CSS_HOTSPOT_ON)) {
            hotspot.classList.remove(CSS_HOTSPOT_ON);
        } else {
            hotspot.classList.add(CSS_HOTSPOT_ON);
        }
    }

    openModal = (evt) => {
        if (evt) {
            evt.preventDefault();
        }
        this.target.classList.add(CSS_MODAL_OPEN);
        this.create();
    }
    closeModal = (evt) => {
        if (evt) {
            evt.preventDefault();
        }
        this.target.classList.remove(CSS_MODAL_OPEN);
        this.destroy();
    }

    loadStart() {
        this.target.querySelector(SELECTOR_LOADER).classList.add(CSS_LOADER_SHOW);
    }
    loadEnd() {
        this.target.querySelector(SELECTOR_LOADER).classList.remove(CSS_LOADER_SHOW);
    }
}

class ABVirtualTour {    
	core;
    constructor(target, options) {
        this.core = new ABVirtualTourCore(target, options);
    }

	init() {
		this.core.init();
	}

    create() {
        this.core.create();
    }

    destroy() {
        this.core.destroy();
    }
}

window.ABVirtualTour = ABVirtualTour;

export default ABVirtualTour;

/*
var Marzipano = require('marzipano')

// Create viewer.
var viewer = new Marzipano.Viewer(document.getElementById('pano'));

// Create source.
var source = Marzipano.ImageUrlSource.fromString(
  "assets/{f}.jpg"
);

// Create geometry.
var geometry = new Marzipano.CubeGeometry([{ tileSize: 1024, size: 1024 }]);

// Create view.
var limiter = Marzipano.RectilinearView.limit.traditional(4096, 100*Math.PI/180);
var view = new Marzipano.RectilinearView(null, limiter);

// Create scene.
var scene = viewer.createScene({
  source: source,
  geometry: geometry,
  view: view,
  pinFirstLevel: true,
});

// hotspot
var imgHotspot = document.createElement('img');
imgHotspot.src = 'assets/loader.gif';
imgHotspot.classList.add('hotspot');
imgHotspot.addEventListener('click', function() {
  console.log('test')
});
var imgPosition = { yaw: Math.PI*2, pitch: Math.PI };
scene.hotspotContainer().createHotspot(imgHotspot, imgPosition);

//hotspot nel dom
var buttonHotspot = document.getElementById('button-test');
var buttonPosition = { yaw: Math.PI, pitch: Math.PI };
scene.hotspotContainer().createHotspot(buttonHotspot, buttonPosition);

// Display scene.
scene.switchTo();

$('.open').on('click',function(){
	$(this).closest('.hotspot').find('.detail').fadeIn();
})
$('.close').on('click',function(){
	$(this).closest('.detail').fadeOut();
})


console.log('test')
*/